import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiUploadCloud, FiEye, FiX } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { PLAYLIST_GET, PLAYLIST_UPDATE } from '../../api/contracts';
import { getApiErrorMessage } from '../../utils/errorMessage';
import MultiSelect from '../MultiSelect';
import './Page.css';
import './CreateCategory.css';
import './Category.css';

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const AGE_GROUP_OPTIONS = [
  { value: '0-3', label: '0-3' },
  { value: '3-6', label: '3-6' },
  { value: '6+', label: '6+' },
];

const THUMB_KEYS = [
  { key: 'small_thumbnail', label: 'Small thumbnail' },
  { key: 'thumbnail_url', label: 'Thumbnail URL' },
  { key: 'thumbnail_url_landscape', label: 'Thumbnail URL (landscape)' },
];

function truncateUrl(url, max = 40) {
  if (!url || typeof url !== 'string') return '—';
  return url.length > max ? url.slice(0, max) + '…' : url;
}

function EditPlaylist() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [age_groups, setAge_groups] = useState([]);
  const [popularity_score, setPopularity_score] = useState('');
  const [rank_score, setRank_score] = useState('');
  const [watchUrl, setWatchUrl] = useState(null);
  const [files, setFiles] = useState({ small_thumbnail: null, thumbnail_url: null, thumbnail_url_landscape: null });
  const [previews, setPreviews] = useState({ small_thumbnail: null, thumbnail_url: null, thumbnail_url_landscape: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError('Invalid playlist.');
      return;
    }
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(apiUrl(PLAYLIST_GET.pathFor(id)), { method: PLAYLIST_GET.method, headers })
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Category not found.' : `Request failed (${res.status}).`);
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        const data = json.data || json;
        setPlaylist(data);
        setTitle(data.title ?? '');
        setStatus(data.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE');
        setAge_groups(Array.isArray(data.age_groups) ? data.age_groups : []);
        setPopularity_score(data.popularity_score != null ? String(data.popularity_score) : '');
        setRank_score(data.rank_score != null ? String(data.rank_score) : '');
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Unable to load playlist. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    return () => {
      THUMB_KEYS.forEach(({ key }) => {
        if (previews[key] && previews[key].startsWith('blob:')) URL.revokeObjectURL(previews[key]);
      });
    };
  }, [previews]);

  const handleFileChange = (key, e) => {
    const file = e.target.files?.[0];
    setError('');
    const prevPreview = previews[key];
    if (prevPreview && prevPreview.startsWith('blob:')) URL.revokeObjectURL(prevPreview);
    if (!file) {
      setFiles((f) => ({ ...f, [key]: null }));
      setPreviews((p) => ({ ...p, [key]: null }));
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      setFiles((f) => ({ ...f, [key]: null }));
      setPreviews((p) => ({ ...p, [key]: null }));
      return;
    }
    setFiles((f) => ({ ...f, [key]: file }));
    setPreviews((p) => ({ ...p, [key]: URL.createObjectURL(file) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Title is required.');
      return;
    }
    const pop = Number(popularity_score);
    const rank = Number(rank_score);
    if (Number.isNaN(pop) || pop < 0) {
      setError('Popularity score must be a valid number.');
      return;
    }
    if (Number.isNaN(rank) || rank < 0) {
      setError('Rank score must be a valid number.');
      return;
    }
    if (!id) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const formData = new FormData();
      formData.append('title', trimmedTitle);
      formData.append('status', status);
      formData.append('popularity_score', String(pop));
      formData.append('rank_score', String(rank));
      age_groups.forEach((v) => formData.append('age_groups', v));
      if (files.small_thumbnail) formData.append('small_thumbnail', files.small_thumbnail);
      if (files.thumbnail_url) formData.append('thumbnail_url', files.thumbnail_url);
      if (files.thumbnail_url_landscape) formData.append('thumbnail_url_landscape', files.thumbnail_url_landscape);

      const res = await fetch(apiUrl(PLAYLIST_UPDATE.pathFor(id)), {
        method: PLAYLIST_UPDATE.method,
        headers,
        body: formData,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status));
        setIsSubmitting(false);
        return;
      }

      navigate('/dashboard/cms/playlist');
    } catch (err) {
      setError(err.message || 'Unable to connect. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page create-category-page">
        <button type="button" className="create-category-back" onClick={() => navigate('/dashboard/cms/playlist')}>
          <FiArrowLeft className="back-icon" aria-hidden />
          Back to Categories
        </button>
        <p className="create-category-loading">Loading playlist…</p>
      </div>
    );
  }

  if (error && !playlist) {
    return (
      <div className="page create-category-page">
        <button type="button" className="create-category-back" onClick={() => navigate('/dashboard/cms/playlist')}>
          <FiArrowLeft className="back-icon" aria-hidden />
          Back to Categories
        </button>
        <div className="create-category-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="page create-category-page">
      <button type="button" className="create-category-back" onClick={() => navigate('/dashboard/cms/playlist')}>
        <FiArrowLeft className="back-icon" aria-hidden />
        Back to Categories
      </button>

      <div className="create-category-header">
        <h3>Edit category</h3>
        <p className="create-category-subtitle">
          Update title, scores or thumbnails. Use the watch icon to view current images; replace only if needed.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="create-category-form">
        <div className="form-page-layout">
          <div className="form-section">
            <h4 className="form-section-title">Details</h4>
            <div className="form-grid-2">
              <div className="form-row-single">
                <label htmlFor="edit-pl-title">Title <span className="label-required" aria-hidden="true">*</span></label>
                <input id="edit-pl-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Food" disabled={isSubmitting} autoComplete="off" />
              </div>
              <div className="form-row-single">
                <label htmlFor="edit-pl-status">Status</label>
                <select id="edit-pl-status" value={status} onChange={(e) => setStatus(e.target.value)} disabled={isSubmitting}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-row-single">
                <label htmlFor="edit-pl-popularity">Popularity score <span className="label-required" aria-hidden="true">*</span></label>
                <input id="edit-pl-popularity" type="number" min={0} value={popularity_score} onChange={(e) => setPopularity_score(e.target.value)} disabled={isSubmitting} />
              </div>
              <div className="form-row-single">
                <label htmlFor="edit-pl-rank">Rank score <span className="label-required" aria-hidden="true">*</span></label>
                <input id="edit-pl-rank" type="number" min={0} value={rank_score} onChange={(e) => setRank_score(e.target.value)} disabled={isSubmitting} />
              </div>
              <div className="form-row-single form-row-full">
                <label>Age groups</label>
                <MultiSelect
                  options={AGE_GROUP_OPTIONS}
                  value={age_groups}
                  onChange={setAge_groups}
                  placeholder="Select age groups"
                  disabled={isSubmitting}
                  searchable={false}
                />
              </div>
            </div>
          </div>

          <div className="form-section form-section-full">
            <h4 className="form-section-title">Thumbnails</h4>
            <div className="form-grid-3">
              {THUMB_KEYS.map(({ key, label }) => (
                <div key={key} className="form-row-single">
                  <label>{label}</label>
                  <div className="thumbnail-upload-area edit-thumb-row">
                    <div className="edit-thumb-current">
                      <span className="edit-thumb-url" title={playlist?.[key]}>{truncateUrl(playlist?.[key])}</span>
                      {playlist?.[key] && (
                        <button type="button" className="btn-watch-inline" onClick={() => setWatchUrl(playlist[key])} title="View current image" aria-label={`View ${label}`}>
                          <FiEye className="watch-icon" />
                        </button>
                      )}
                    </div>
                    <div className="edit-thumb-replace">
                      <input type="file" id={`edit-pl-${key}`} accept="image/*" onChange={(e) => handleFileChange(key, e)} disabled={isSubmitting} className="thumbnail-input" />
                      <label htmlFor={`edit-pl-${key}`} className="thumbnail-upload-label thumbnail-upload-label-small">
                        <FiUploadCloud className="upload-icon" aria-hidden />
                        <span>Replace</span>
                      </label>
                      {previews[key] && (
                        <div className="thumbnail-preview-wrap thumbnail-preview-wrap-small">
                          <img src={previews[key]} alt={`New ${label}`} className="thumbnail-preview-img" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {error && <div className="create-category-error" role="alert">{error}</div>}

        <div className="form-actions form-actions-full">
          <button type="button" className="btn-cancel" onClick={() => navigate('/dashboard/cms/playlist')} disabled={isSubmitting}>Cancel</button>
          <button type="submit" className="btn-submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>

      {watchUrl && (
        <div className="thumbnail-overlay" onClick={() => setWatchUrl(null)} role="dialog" aria-modal="true" aria-label="Thumbnail preview">
          <div className="thumbnail-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn-close-preview" onClick={() => setWatchUrl(null)} aria-label="Close preview">
              <FiX className="close-icon" />
            </button>
            <img src={watchUrl} alt="Thumbnail" className="thumbnail-preview-img" />
          </div>
        </div>
      )}
    </div>
  );
}

export default EditPlaylist;
